import * as React from 'react'
import { useState } from 'react'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { Button } from '@pushly/aqe/lib/components'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import { Flex, Modal, Table } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import { PageAnalysisFieldsService } from '@halo/services/page-analysis-fields.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { snakeToHumanCase } from '@halo/_utils/string'
import { DeleteOutlined, EditOutlined, FileSearchOutlined } from '@ant-design/icons'
import { IPageAnalysisField } from '@pushly/aqe/lib/interfaces'
import './style/page-analysis-field-configurations.tab.scss'

export interface IPageAnalysisDetailsTabProps {
    hydratedPageAnalysisConfig: IHydratedPageAnalysisConfig
    onUpdate: (update: IHydratedPageAnalysisConfig) => void
}

function PageAnalysisFieldConfigsTab(props: IPageAnalysisDetailsTabProps) {
    const appService = Container.get(AppService)
    const pageAnalysisFieldsService: PageAnalysisFieldsService = Container.get(PageAnalysisFieldsService)
    const [isDeleting, setIsDeleting] = useState(false)
    const { hydratedPageAnalysisConfig } = props

    function isLocal(record: IPageAnalysisField) {
        return record.page_analysis_config_id === hydratedPageAnalysisConfig?.id
    }

    function renderRowActions(_: any, record: IPageAnalysisField): React.ReactNode {
        if (!isLocal(record)) return null

        const dropdownActions: ButtonAction[] = [
            {
                key: 'delete',
                text: 'Delete',
                icon: <DeleteOutlined />,
                onClick: () => del(record),
            },
        ]

        return (
            <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                    appService.route(
                        `/page-analysis-configs/${record.page_analysis_config_id}/field-configs/${record.id}`,
                    )
                }
                actions={dropdownActions}
            >
                Edit
            </Button>
        )
    }

    function del(pageAnalysisField: IPageAnalysisField) {
        Modal.confirm({
            title: 'Delete Page Analysis Field',
            content: <p>Are you sure you want to delete this page analysis field?</p>,
            onOk: async () => {
                try {
                    setIsDeleting(true)
                    await pageAnalysisFieldsService.delete(hydratedPageAnalysisConfig.id, pageAnalysisField.id)
                    hydratedPageAnalysisConfig.fields = hydratedPageAnalysisConfig.fields.filter(
                        (f) => f.id !== pageAnalysisField.id,
                    )
                    props.onUpdate(hydratedPageAnalysisConfig as IHydratedPageAnalysisConfig)
                } catch (e) {
                    simpleNotification('error', e.toString())
                } finally {
                    setIsDeleting(false)
                }
            },
        })
    }

    const tableHeader = (
        <Flex gap={8} justify="flex-end" align="center">
            <Button
                type="primary"
                disabled={isDeleting || !hydratedPageAnalysisConfig}
                onClick={() =>
                    appService.route(`/page-analysis-configs/${hydratedPageAnalysisConfig.id}/field-configs/new`)
                }
            >
                Add Field Config
            </Button>
        </Flex>
    )

    return (
        <Table<IPageAnalysisField>
            className="aqe-table-well page-analysis-field-configurations-list"
            title={() => tableHeader}
            size="small"
            bordered={false}
            pagination={false}
            dataSource={hydratedPageAnalysisConfig?.fields}
            rowKey={(record) => record.id.toString()}
            rowClassName={(record) => (isLocal(record) ? 'local' : 'inherited')}
            loading={!hydratedPageAnalysisConfig}
            locale={{
                emptyText: <AntdTableEmptyPlaceholder text="No Field Configs" icon={<FileSearchOutlined />} />,
            }}
        >
            <Table.Column className="id" title="ID" dataIndex="id" />
            <Table.Column className="priority" title="Priority" dataIndex="priority" />
            <Table.Column
                className="field"
                title="Field"
                render={(record: IPageAnalysisField) =>
                    record.name?.startsWith('additional_fields.') ? record.name : snakeToHumanCase(record.name)
                }
            />
            <Table.Column className="data-type" title="Data Type" dataIndex="type" />
            <Table.Column
                className="strategies"
                title="Strategies"
                render={(_text, record: IPageAnalysisField) => {
                    const counts = (record.strategies || []).reduce<Record<string, number>>((acc, val) => {
                        acc[val.type] = (acc[val.type] ?? 0) + 1
                        return acc
                    }, {})
                    return Object.keys(counts)
                        .sort()
                        .map((k) => (
                            <span key={k}>
                                {k} ({counts[k]}){' '}
                            </span>
                        ))
                }}
            />
            <Table.Column className="actions" title="Actions" width={180} render={renderRowActions} />
        </Table>
    )
}

export default PageAnalysisFieldConfigsTab
